import { createContext, useCallback, useContext, useMemo } from 'react';
import { useDisclosure, useLocalStorage } from '@mantine/hooks';

const STORAGE_KEY = 'yogi-foods-cookies-consent';

// Schema version. Bump this whenever the consent shape changes so
// returning users get re-prompted under the new schema rather than silently
// grandfathering into it.
const SCHEMA_VERSION = 1;

const ConsentContext = createContext(null);

function normalise(raw) {
  if (!raw || typeof raw !== 'object') return null;
  if (raw.v !== SCHEMA_VERSION) return null;
  if (
    typeof raw.essential !== 'boolean' ||
    typeof raw.analytics !== 'boolean' ||
    typeof raw.marketing !== 'boolean'
  ) {
    return null;
  }
  return raw;
}

export function CookieConsentProvider({ children }) {
  const [rawConsent, setRawConsent] = useLocalStorage({
    key: STORAGE_KEY,
    defaultValue: null,
  });
  const [drawerOpen, drawerHandlers] = useDisclosure(false);

  const consent = normalise(rawConsent);
  const isFullyDecided = consent !== null;

  const stamp = useCallback(
    (overrides) => ({
      v: SCHEMA_VERSION,
      essential: true,
      analytics: false,
      marketing: false,
      ...overrides,
    }),
    []
  );

  const acceptAll = useCallback(() => {
    setRawConsent(stamp({ analytics: true, marketing: true }));
    drawerHandlers.close();
  }, [setRawConsent, stamp, drawerHandlers]);

  const rejectAll = useCallback(() => {
    setRawConsent(stamp({ analytics: false, marketing: false }));
    drawerHandlers.close();
  }, [setRawConsent, stamp, drawerHandlers]);

  const savePreferences = useCallback(
    (next) => {
      setRawConsent(
        stamp({
          analytics: Boolean(next?.analytics),
          marketing: Boolean(next?.marketing),
        })
      );
      drawerHandlers.close();
    },
    [setRawConsent, stamp, drawerHandlers]
  );

  const openPreferences = useCallback(() => {
    drawerHandlers.open();
  }, [drawerHandlers]);

  const closePreferences = useCallback(() => {
    drawerHandlers.close();
  }, [drawerHandlers]);

  const value = useMemo(
    () => ({
      consent,
      isFullyDecided,
      drawerOpen,
      openPreferences,
      closePreferences,
      acceptAll,
      rejectAll,
      savePreferences,
      SCHEMA_VERSION,
    }),
    [
      consent,
      isFullyDecided,
      drawerOpen,
      openPreferences,
      closePreferences,
      acceptAll,
      rejectAll,
      savePreferences,
    ]
  );

  return (
    <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const ctx = useContext(ConsentContext);
  if (ctx === null) {
    throw new Error('useCookieConsent must be used within CookieConsentProvider');
  }
  return ctx;
}